import { ArrowRight, CalendarClock, Loader2, Pencil, Play, Plus, Trash2, UserCog, Workflow } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Section } from '@/components/common/Blocks'
import { PageHeader } from '@/components/common/PageHeader'
import { InfoNote } from '@/components/common/Panel'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { confirmModal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useAccessProjects } from '@/features/access/api'
import { useCustomers } from '@/features/crm/api'
import { apiErrorMessage } from '@/i18n'
import { isApiError } from '@/lib/api/errors'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import { BOARD_PRIORITY_TONE } from '../boards/board'
import { RULE_EVENTS, type RuleInput, type TaskRule, useRuleGroups, useRuleMutations, useTaskRules } from './api'
import { RoleAssignmentPicker } from './RoleAssignmentPicker'
import { RuleEditorModal } from './RuleEditorModal'
import { type RuleLookups, useRuleLookups } from './ruleLookups'

const EMPTY: RuleInput = {
  event: 'manual',
  matchType: 'group',
  matchValue: '',
  title: '',
  taskType: '',
  description: '',
  assigneeRole: '',
  boardCode: 'teknik',
  columnKey: 'backlog',
  priority: 'normal',
  dueInDays: null,
  position: 0,
  isActive: true,
}

/**
 * Görev kuralları: bir olay olunca (demo oluşturuldu, sipariş onaylandı…) ya da elle çalıştırılınca açılacak görevler.
 * Her kural "ne zaman → hangi durumda → hangi görev → nereye ve kime" sorularıyla tanımlanır; teknik kodlar (pano,
 * kolon, aşama, modül) sistemden seçilir.
 */
export function TaskRulesPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const canManage = can('tasks.boards.manage')
  const rules = useTaskRules()
  const lookups = useRuleLookups()
  const { remove } = useRuleMutations()
  const [edit, setEdit] = useState<{ id?: string; input: RuleInput } | null>(null)

  const onDelete = async (r: TaskRule) => {
    if (!(await confirmModal({ title: t('projects.rules.deleteTitle'), description: r.title, confirmLabel: t('common.delete'), destructive: true }))) return
    remove.mutate(r.id, { onSuccess: () => toast.success(t('common.deleted')), onError: (e) => toast.error(apiErrorMessage(e)) })
  }
  // Elle çalıştırılan kurallar grup grup, olay kuralları olay olay listelenir.
  const manual = (rules.data ?? []).filter((r) => r.event === 'manual')
  const manualGroups = [...new Set(manual.map((r) => r.matchValue))].sort((a, b) => a.localeCompare(b, 'tr'))
  const sections = [
    ...RULE_EVENTS.filter((ev) => ev !== 'manual').map((ev) => ({ key: ev, title: t(`projects.rules.events.${ev}`), hint: t(`projects.rules.eventHints.${ev}`), items: (rules.data ?? []).filter((r) => r.event === ev) })),
    ...manualGroups.map((g) => ({ key: `group:${g}`, title: t('projects.rules.groupTitle', { group: g }), hint: t('projects.rules.eventHints.manual'), items: manual.filter((r) => r.matchValue === g) })),
  ].filter((s) => s.items.length)

  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.projects')}
        title={t('projects.rules.title')}
        description={t('projects.rules.subtitle')}
        actions={
          canManage ? (
            <Button onClick={() => setEdit({ input: { ...EMPTY, boardCode: lookups.boards.find((b) => b.isDefault)?.code ?? EMPTY.boardCode } })}>
              <Plus />
              {t('projects.rules.add')}
            </Button>
          ) : null
        }
      />
      <div className="space-y-4">
        <InfoNote title={t('projects.rules.howTitle')}>{t('projects.rules.how')}</InfoNote>
        <RunPanel />
        {rules.isError ? (
          <ErrorState error={rules.error} onRetry={() => void rules.refetch()} />
        ) : rules.isLoading ? (
          <Skeleton className="h-48 rounded-xl" />
        ) : !sections.length ? (
          <EmptyState icon={Workflow} title={t('projects.rules.empty')} description={t('projects.rules.emptyHint')} />
        ) : (
          sections.map((g) => (
            <Section
              key={g.key}
              icon={Workflow}
              bodyClassName="p-0"
              title={
                <span className="block">
                  {g.title}
                  <span className="block text-xs font-normal text-muted-foreground">{g.hint}</span>
                </span>
              }
            >
              <ul className="divide-y">
                {g.items.map((r) => (
                  <RuleRow key={r.id} rule={r} lookups={lookups} canManage={canManage} onEdit={() => setEdit({ id: r.id, input: { ...r } })} onDelete={() => void onDelete(r)} />
                ))}
              </ul>
            </Section>
          ))
        )}
      </div>
      {edit ? <RuleEditorModal id={edit.id} initial={edit.input} lookups={lookups} onClose={() => setEdit(null)} /> : null}
    </>
  )
}

function RuleRow({ rule, lookups, canManage, onEdit, onDelete }: { rule: TaskRule; lookups: RuleLookups; canManage: boolean; onEdit: () => void; onDelete: () => void }) {
  const { t } = useTranslation()
  const condition = lookups.conditionLabel(rule)
  return (
    <li className={cn('flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3', !rule.isActive && 'opacity-60')}>
      <div className="min-w-0 flex-1 basis-72 space-y-1.5">
        <p className="text-sm font-medium">{rule.title.replace(/\{(account|module|stage|meeting_kind|days)\}/g, (_, v: string) => `‹${t(`projects.rules.vars.${v}`)}›`)}</p>
        <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          {condition ? <Badge tone="neutral">{condition}</Badge> : null}
          <span className="inline-flex items-center gap-1">
            <ArrowRight className="size-3" aria-hidden />
            {lookups.placeLabel(rule)}
          </span>
          <span className="inline-flex items-center gap-1">
            <UserCog className="size-3" aria-hidden />
            {rule.assigneeRole || t('projects.rules.defaultAssignee')}
          </span>
          {rule.dueInDays != null ? (
            <span className="inline-flex items-center gap-1">
              <CalendarClock className="size-3" aria-hidden />
              {t('projects.rules.dueDays', { count: rule.dueInDays })}
            </span>
          ) : null}
        </div>
      </div>
      <span className={cn('rounded-md px-1.5 py-0.5 text-[11px] font-semibold', BOARD_PRIORITY_TONE[rule.priority])}>{t(`projects.rules.priority.${rule.priority}`)}</span>
      {!rule.isActive ? <Badge tone="warning">{t('projects.rules.inactive')}</Badge> : null}
      {canManage ? (
        <div className="flex">
          <Button size="icon" variant="ghost" className="size-8" aria-label={t('common.edit')} onClick={onEdit}>
            <Pencil />
          </Button>
          <Button size="icon" variant="ghost" className="size-8" aria-label={t('common.delete')} onClick={onDelete}>
            <Trash2 />
          </Button>
        </div>
      ) : null}
    </li>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  )
}

/** Bir kural grubunu müşteri ya da proje için çalıştırır; önce rollere personel seçilir. */
function RunPanel() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const groups = useRuleGroups()
  const customers = useCustomers()
  const projects = useAccessProjects()
  const { run } = useRuleMutations()
  const [group, setGroup] = useState('')
  const [target, setTarget] = useState<{ kind: 'account' | 'project'; id: string }>({ kind: 'account', id: '' })
  const [assignments, setAssignments] = useState<Record<string, string>>({})
  if (!can('tasks.assign') || !groups.data?.length) return null
  const targetOptions = target.kind === 'account' ? (customers.data ?? []).map((c) => ({ value: c.id, label: c.name })) : (projects.data ?? []).map((p) => ({ value: p.id, label: p.name }))
  const onRun = (force = false) =>
    run.mutate(
      { group, accountId: target.kind === 'account' ? target.id : undefined, projectId: target.kind === 'project' ? target.id : undefined, assignments, force },
      {
        onSuccess: (r) => {
          toast.success(t('projects.rules.run.done', { count: r?.task_ids?.length ?? 0 }))
          setAssignments({})
        },
        onError: async (e) => {
          // Grup bu müşteri/proje için zaten çalıştırılmışsa görevler çiftlenmesin diye onay istenir.
          if (isApiError(e) && e.code === 'RULE_ALREADY_RUN') {
            if (await confirmModal({ title: t('projects.rules.run.againTitle'), description: e.message, confirmLabel: t('projects.rules.run.again') })) onRun(true)
            return
          }
          toast.error(apiErrorMessage(e))
        },
      },
    )
  return (
    <Section title={t('projects.rules.run.title')} icon={Play}>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Field label={t('projects.rules.f.group')}>
          <Select value={group} onChange={(v) => { setGroup(v); setAssignments({}) }} options={groups.data.map((g) => ({ value: g, label: g }))} placeholder={t('projects.rules.run.pickGroup')} />
        </Field>
        <Field label={t('projects.rules.run.for')}>
          <Select value={target.kind} onChange={(v) => setTarget({ kind: v as 'account' | 'project', id: '' })} options={[{ value: 'account', label: t('projects.rules.run.account') }, { value: 'project', label: t('projects.rules.run.project') }]} />
        </Field>
        <Field label={t(target.kind === 'account' ? 'projects.rules.run.account' : 'projects.rules.run.project')}>
          <Select searchable value={target.id} onChange={(v) => setTarget((x) => ({ ...x, id: v }))} options={targetOptions} placeholder={t('projects.rules.run.pickTarget')} />
        </Field>
      </div>
      {group && target.id ? (
        <div className="mt-3">
          <RoleAssignmentPicker
            event="manual"
            group={group}
            accountId={target.kind === 'account' ? target.id : undefined}
            projectId={target.kind === 'project' ? target.id : undefined}
            value={assignments}
            onChange={setAssignments}
          />
        </div>
      ) : null}
      <div className="mt-3 flex justify-end">
        <Button onClick={() => onRun()} disabled={!group || !target.id || run.isPending}>
          {run.isPending ? <Loader2 className="animate-spin" /> : <Play />}
          {t('projects.rules.run.submit')}
        </Button>
      </div>
    </Section>
  )
}
